import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, TriangleAlert, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { api, type Insights as InsightsData } from '../lib/api';
import { Spinner } from './ui';

/** Tableau de bord des retours (D) — réservé ADMIN. Ce qu'on cherche, ce qui ne
 *  trouve rien (à nourrir), et la satisfaction. */
export function Insights({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<InsightsData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    void api
      .insights()
      .then(setData)
      .catch(() => setError(true));
  }, []);

  const fmtDate = (iso: string): string =>
    new Date(iso).toLocaleDateString(i18n.language === 'de' ? 'de-CH' : 'fr-CH', { day: '2-digit', month: 'short' });

  const stat = (Icon: typeof ThumbsUp, value: number, label: string, accent?: string) => (
    <div className="rounded-xl bg-surface-2 px-3 py-2.5">
      <p className={`flex items-center gap-1.5 text-xl font-semibold ${accent ?? ''}`}>
        <Icon className="h-4 w-4" /> {value}
      </p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-30 grid place-items-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('insights.title')}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">{t('insights.title')}</h2>
            <p className="text-xs text-muted">{t('insights.period')}</p>
          </div>
          <button onClick={onClose} aria-label={t('insights.close')} className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {error ? (
            <p className="text-sm text-muted">{t('error.generic')}</p>
          ) : !data ? (
            <Spinner label={t('explore.loading')} />
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {stat(MessageSquare, data.counts.total, t('insights.total'))}
                {stat(TriangleAlert, data.counts.gaps, t('insights.gaps'), 'text-amber-600 dark:text-amber-400')}
                {stat(ThumbsUp, data.counts.up, t('insights.up'), 'text-emerald-600 dark:text-emerald-400')}
                {stat(ThumbsDown, data.counts.down, t('insights.down'), 'text-red-600 dark:text-red-400')}
              </div>

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{t('insights.gapsTitle')}</h3>
                {data.recentGaps.length === 0 ? (
                  <p className="text-sm text-muted">{t('insights.gapsEmpty')}</p>
                ) : (
                  <ul className="space-y-1">
                    {data.recentGaps.map((g) => (
                      <li key={g.id} className="flex items-center gap-2 rounded-lg bg-amber-500/5 px-3 py-1.5 text-sm ring-1 ring-amber-500/20">
                        <span className="min-w-0 flex-1 truncate">{g.question}</span>
                        <span className="shrink-0 text-xs text-muted">{fmtDate(g.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {data.top.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{t('insights.topTitle')}</h3>
                  <ul className="space-y-1">
                    {data.top.map((q, i) => (
                      <li key={i} className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-1.5 text-sm">
                        <span className="min-w-0 flex-1 truncate">{q.question}</span>
                        <span className="shrink-0 rounded-full bg-surface px-2 text-xs text-muted">×{q.n}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
